import { createStore, type Store, type StoreActionApi } from 'react-sweet-state';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

export type SelectedAgentDraftMetadata = {
	/** Present when the routing ID is already known; restored editor mentions only have an AAID. */
	id?: string;
	name?: string;
	identityAccountId?: string;
	creatorType?: string;
};

export type SelectedAgentDraftControllerStore = {
	/**
	 * Per-conversation, unsent chat-prompt selected-agent history.
	 *
	 * This is prompt-input UI state for routing the current prompt submission, not
	 * the shared prompt-text draft or persisted conversation state. The store itself
	 * is intentionally ungated; `rovo_chat_mention_agents` enforcement belongs at
	 * consumers that read or route with this state.
	 *
	 * The active selected agent is always the last entry; consumers must not read
	 * `[0]` as the active id. With the experiment in variation, ids are unique
	 * and ordered by activation recency. The control path preserves the legacy
	 * duplicate-selection behavior.
	 */
	selectedAgentIds: { [conversationId: string]: string[] | undefined };
	deactivatedAgentIds: { [conversationId: string]: string[] | undefined };
	selectedAgentMetadata: {
		[conversationId: string]:
			| { [agentId: string]: SelectedAgentDraftMetadata | undefined }
			| undefined;
	};
};

const normalizeAgentId = (agentId: string): string | undefined => {
	const trimmedAgentId = agentId.trim();
	return trimmedAgentId.length > 0 ? trimmedAgentId : undefined;
};

/** Preserve the legacy gate-off behavior for duplicate selections. */
const removeMostRecentSelectedAgentId = (
	agentIds: readonly string[],
	agentId: string,
): string[] => {
	const normalizedAgentId = normalizeAgentId(agentId);
	if (!normalizedAgentId) {
		return agentIds as string[];
	}

	for (let index = agentIds.length - 1; index >= 0; index--) {
		if (agentIds[index] === normalizedAgentId) {
			return [...agentIds.slice(0, index), ...agentIds.slice(index + 1)];
		}
	}

	return agentIds as string[];
};

const deduplicateAgentIdsByLastActivation = (agentIds: readonly string[]): string[] => {
	const seenAgentIds = new Set<string>();
	const uniqueAgentIds: string[] = [];

	for (let index = agentIds.length - 1; index >= 0; index--) {
		const agentId = agentIds[index];
		if (!seenAgentIds.has(agentId)) {
			seenAgentIds.add(agentId);
			uniqueAgentIds.push(agentId);
		}
	}

	return uniqueAgentIds.reverse();
};

const keepMetadataForSelectedAgentIds = (
	metadata: { [agentId: string]: SelectedAgentDraftMetadata | undefined } | undefined,
	agentIds: string[],
): { [agentId: string]: SelectedAgentDraftMetadata | undefined } | undefined => {
	if (!metadata) {
		return undefined;
	}

	const selectedAgentIdSet = new Set(agentIds);
	const nextMetadata = Object.fromEntries(
		Object.entries(metadata).filter(([agentId]) => selectedAgentIdSet.has(agentId)),
	);
	return Object.keys(nextMetadata).length > 0 ? nextMetadata : undefined;
};

type SelectedAgentDraftControllerStoreActions = {
	setSelectedAgentIds: (
		conversationId: string,
		agentIds: string[],
	) => ({ setState, getState }: StoreActionApi<SelectedAgentDraftControllerStore>) => void;
	addSelectedAgentId: (
		conversationId: string,
		agentId: string,
		metadata?: SelectedAgentDraftMetadata,
	) => ({ setState, getState }: StoreActionApi<SelectedAgentDraftControllerStore>) => void;
	removeSelectedAgentId: (
		conversationId: string,
		agentId: string,
	) => ({ setState, getState }: StoreActionApi<SelectedAgentDraftControllerStore>) => void;
};

const initialState: SelectedAgentDraftControllerStore = {
	selectedAgentIds: {},
	deactivatedAgentIds: {},
	selectedAgentMetadata: {},
};

export const selectedAgentDraftControllerStore: Store<
	SelectedAgentDraftControllerStore,
	SelectedAgentDraftControllerStoreActions
> = createStore<SelectedAgentDraftControllerStore, SelectedAgentDraftControllerStoreActions>({
	initialState,
	actions: {
		setSelectedAgentIds:
			(conversationId: string, agentIds: string[]) =>
			({ setState, getState }) => {
				const { selectedAgentIds, deactivatedAgentIds, selectedAgentMetadata } = getState();
				const isMentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');
				const normalizedAgentIdsWithDuplicates = agentIds.flatMap((agentId) => {
					const normalizedAgentId = normalizeAgentId(agentId);
					return normalizedAgentId ? [normalizedAgentId] : [];
				});
				const normalizedAgentIds = isMentionAgentsExperimentEnabled
					? deduplicateAgentIdsByLastActivation(normalizedAgentIdsWithDuplicates)
					: normalizedAgentIdsWithDuplicates;
				const { [conversationId]: _removedDeactivatedAgentIds, ...nextDeactivatedAgentIds } =
					deactivatedAgentIds;

				if (normalizedAgentIds.length === 0) {
					const { [conversationId]: _removedSelectedAgentIds, ...nextSelectedAgentIds } =
						selectedAgentIds;
					const { [conversationId]: _removedSelectedAgentMetadata, ...nextSelectedAgentMetadata } =
						selectedAgentMetadata;
					setState({
						selectedAgentIds: nextSelectedAgentIds,
						deactivatedAgentIds: nextDeactivatedAgentIds,
						selectedAgentMetadata: nextSelectedAgentMetadata,
					});
					return;
				}

				const nextSelectedAgentMetadata = keepMetadataForSelectedAgentIds(
					selectedAgentMetadata[conversationId],
					normalizedAgentIds,
				);
				const { [conversationId]: _removedSelectedAgentMetadata, ...baseSelectedAgentMetadata } =
					selectedAgentMetadata;

				setState({
					selectedAgentIds: {
						...selectedAgentIds,
						[conversationId]: normalizedAgentIds,
					},
					deactivatedAgentIds: nextDeactivatedAgentIds,
					selectedAgentMetadata: nextSelectedAgentMetadata
						? {
								...baseSelectedAgentMetadata,
								[conversationId]: nextSelectedAgentMetadata,
							}
						: baseSelectedAgentMetadata,
				});
			},
		addSelectedAgentId:
			(conversationId: string, agentId: string, metadata?: SelectedAgentDraftMetadata) =>
			({ setState, getState }) => {
				const normalizedAgentId = normalizeAgentId(agentId);
				if (!normalizedAgentId) {
					return;
				}

				const { selectedAgentIds, deactivatedAgentIds, selectedAgentMetadata } = getState();
				const currentSelectedAgentIds = selectedAgentIds[conversationId] ?? [];
				const isMentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');
				const nextSelectedAgentIds = isMentionAgentsExperimentEnabled
					? [
							...currentSelectedAgentIds.filter(
								(selectedAgentId) => selectedAgentId !== normalizedAgentId,
							),
							normalizedAgentId,
						]
					: [...currentSelectedAgentIds, normalizedAgentId];
				const nextDeactivatedAgentIdsForConversation: string[] =
					deactivatedAgentIds[conversationId]?.filter(
						(deactivatedAgentId) => deactivatedAgentId !== normalizedAgentId,
					) ?? [];
				const { [conversationId]: _removedDeactivatedAgentIds, ...baseDeactivatedAgentIds } =
					deactivatedAgentIds;
				setState({
					selectedAgentIds: {
						...selectedAgentIds,
						[conversationId]: nextSelectedAgentIds,
					},
					...(isMentionAgentsExperimentEnabled
						? {
								deactivatedAgentIds:
									nextDeactivatedAgentIdsForConversation.length > 0
										? {
												...baseDeactivatedAgentIds,
												[conversationId]: nextDeactivatedAgentIdsForConversation,
											}
										: baseDeactivatedAgentIds,
							}
						: {}),
					...(metadata
						? {
								selectedAgentMetadata: {
									...selectedAgentMetadata,
									[conversationId]: {
										...selectedAgentMetadata[conversationId],
										[normalizedAgentId]: metadata,
									},
								},
							}
						: {}),
				});
			},
		removeSelectedAgentId:
			(conversationId: string, agentId: string) =>
			({ setState, getState }) => {
				const normalizedAgentId = normalizeAgentId(agentId);
				if (!normalizedAgentId) {
					return;
				}

				const { selectedAgentIds, deactivatedAgentIds, selectedAgentMetadata } = getState();
				const currentSelectedAgentIds = selectedAgentIds[conversationId];
				const isMentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');

				if (isMentionAgentsExperimentEnabled) {
					const nextSelectedAgentIds =
						currentSelectedAgentIds?.filter(
							(selectedAgentId) => selectedAgentId !== normalizedAgentId,
						) ?? [];
					const currentDeactivatedAgentIds = deactivatedAgentIds[conversationId] ?? [];
					const nextDeactivatedAgentIds: string[] = currentDeactivatedAgentIds.includes(
						normalizedAgentId,
					)
						? currentDeactivatedAgentIds
						: [...currentDeactivatedAgentIds, normalizedAgentId];
					const {
						[conversationId]: _removedSelectedAgentIds,
						...nextSelectedAgentIdsByConversation
					} = selectedAgentIds;
					const { [conversationId]: _removedSelectedAgentMetadata, ...nextSelectedAgentMetadata } =
						selectedAgentMetadata;
					const retainedSelectedAgentMetadata = keepMetadataForSelectedAgentIds(
						selectedAgentMetadata[conversationId],
						nextSelectedAgentIds,
					);
					setState({
						selectedAgentIds:
							nextSelectedAgentIds.length > 0
								? {
										...nextSelectedAgentIdsByConversation,
										[conversationId]: nextSelectedAgentIds,
									}
								: nextSelectedAgentIdsByConversation,
						deactivatedAgentIds: {
							...deactivatedAgentIds,
							[conversationId]: nextDeactivatedAgentIds,
						},
						selectedAgentMetadata: retainedSelectedAgentMetadata
							? {
									...nextSelectedAgentMetadata,
									[conversationId]: retainedSelectedAgentMetadata,
								}
							: nextSelectedAgentMetadata,
					});
					return;
				}

				if (!currentSelectedAgentIds) {
					return;
				}
				const nextSelectedAgentIds = removeMostRecentSelectedAgentId(
					currentSelectedAgentIds,
					normalizedAgentId,
				);
				if (nextSelectedAgentIds === currentSelectedAgentIds) {
					return;
				}
				if (nextSelectedAgentIds.length === 0) {
					const {
						[conversationId]: _removedSelectedAgentIds,
						...nextSelectedAgentIdsByConversation
					} = selectedAgentIds;
					const { [conversationId]: _removedSelectedAgentMetadata, ...nextSelectedAgentMetadata } =
						selectedAgentMetadata;
					setState({
						selectedAgentIds: nextSelectedAgentIdsByConversation,
						selectedAgentMetadata: nextSelectedAgentMetadata,
					});
					return;
				}

				const nextSelectedAgentMetadata = keepMetadataForSelectedAgentIds(
					selectedAgentMetadata[conversationId],
					nextSelectedAgentIds,
				);
				const { [conversationId]: _removedSelectedAgentMetadata, ...baseSelectedAgentMetadata } =
					selectedAgentMetadata;

				setState({
					selectedAgentIds: {
						...selectedAgentIds,
						[conversationId]: nextSelectedAgentIds,
					},
					selectedAgentMetadata: nextSelectedAgentMetadata
						? {
								...baseSelectedAgentMetadata,
								[conversationId]: nextSelectedAgentMetadata,
							}
						: baseSelectedAgentMetadata,
				});
			},
	},
});
